import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { serviceHelper } from '../../config/service-helper';
import { ArticleTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ArticleTransformer';
import { ContentfulArticle, ContentfulCompetition, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { CompetitionTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CompetitionTransformer';
import { getLocalTimeWithoutTimezone } from '@jbhi-fi/utils/src/date';
import { QuickSearchContentProps } from '@jbhi-fi/components/src/QuickSearchContent/QuickSearchContent';

const MAX_SEARCH_RESULTS = 6;

const contentfulClient = new ContentfulThemeApiClient({
  themeApiBaseUrl: serviceHelper.themeApisUrl,
});

export const getSearchContentSuggestions = async (searchTerm: string): Promise<Array<QuickSearchContentProps>> => {
  const [articleHeadingEntries, articleIntroEntries, competitionHeadingEntries] = await Promise.all([
    contentfulClient.getEntries<ContentfulArticle>({
      content_type: EntryType.article,
      'fields.heading[match]': searchTerm,
      limit: MAX_SEARCH_RESULTS.toString(),
      include: '2',
      order: '-fields.publishedDate',
    }),
    contentfulClient.getEntries<ContentfulArticle>({
      content_type: EntryType.article,
      'fields.intro[match]': searchTerm,
      limit: MAX_SEARCH_RESULTS.toString(),
      include: '2',
      order: '-fields.publishedDate',
    }),
    contentfulClient.getEntries<ContentfulCompetition>({
      content_type: EntryType.competition,
      'fields.heading[match]': searchTerm,
      'fields.endDate[gte]': getLocalTimeWithoutTimezone(new Date()),
      limit: MAX_SEARCH_RESULTS.toString(),
      include: '2',
      order: 'fields.endDate',
    }),
  ]);

  const articleEntries = [...(articleHeadingEntries?.data?.items || []), ...(articleIntroEntries?.data?.items || [])];
  const uniqueSortedArticleEntries = Array.from(
    articleEntries.reduce((map, item) => map.set(item.sys.id, item), new Map()).values()
  ).sort(
    (a, b) =>
      new Date(b.fields?.publishedDate ?? b.sys.createdAt ?? 0).getTime() -
      new Date(a.fields?.publishedDate ?? a.sys.createdAt ?? 0).getTime()
  );

  const transformedArticles = uniqueSortedArticleEntries.map(item => ({
    ...ArticleTransformer.toQuickSearchCardProps(item),
    searchTerm,
  }));

  const transformedCompetitions =
    competitionHeadingEntries?.data?.items.map(item => ({
      ...CompetitionTransformer.toQuickSearchCardProps(item),
      searchTerm,
    })) || [];

  return [...transformedCompetitions, ...transformedArticles].slice(0, MAX_SEARCH_RESULTS);
};
